import React from "react";
import ReactPlayer from "react-player/lazy";

import { Box, Button, Modal, Stack } from "@mui/material";
import welcomeVideoPoster from "assets/images/welcome-video-poster.png";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useDevice } from "hooks";
import { useWelcomeVideoUrlSelector } from "store";

interface Props {
  isOpen: boolean;
  close: () => void;
  onGetStarted?: () => void;
}

const WELCOME_MODAL_CONTENT = {
  title: "Welcome to Altius",
  description:
    "Get started with a quick introduction to the platform. Learn about key features and how to make the most of your experience.",
  buttonText: "Get Started"
};

export const WelcomeModal: React.FC<Props> = ({ isOpen, close, onGetStarted }) => {
  const { isMobile } = useDevice();
  const videoUrl = useWelcomeVideoUrlSelector();

  const handleGetStarted = () => {
    onGetStarted?.();
    close();
  };

  if (!videoUrl) {
    return null;
  }

  return (
    <Modal open={isOpen} onClose={close}>
      <Stack
        sx={{
          width: isMobile ? "90%" : "600px",
          maxWidth: "600px",
          p: isMobile ? "20px" : "30px",
          boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
          bgcolor: "white",
          borderRadius: "8px",
          gap: "20px",
          maxHeight: isMobile ? "90vh" : "auto",
          overflow: "auto",
          ...modalStyle
        }}
      >
        <Box
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            pb: "10px",
            borderBottom: "1px solid #DAE0E2"
          }}
        >
          {WELCOME_MODAL_CONTENT.title}
        </Box>

        <Stack gap="16px" alignItems="stretch">
          <Box
            sx={{
              width: "100%",
              aspectRatio: "16 / 9",
              bgcolor: "#000",
              borderRadius: "8px",
              overflow: "hidden"
            }}
          >
            <ReactPlayer
              url={videoUrl}
              width="100%"
              height="100%"
              controls
              light={welcomeVideoPoster}
              config={{
                youtube: {
                  playerVars: { modestbranding: 1 }
                }
              }}
            />
          </Box>

          <Box
            sx={{
              fontSize: isMobile ? "14px" : "16px",
              color: "#5d7782",
              textAlign: "center",
              px: isMobile ? "10px" : "20px"
            }}
          >
            {WELCOME_MODAL_CONTENT.description}
          </Box>
        </Stack>

        <Stack
          direction={isMobile ? "column" : "row"}
          gap="12px"
          justifyContent="space-between"
          sx={{ borderTop: "1px solid #DAE0E2", pt: "16px" }}
        >
          <MaterialTooltip title="This overview will appear again on your next login." isShow placement="top">
            <Button
              onClick={close}
              variant="text"
              fullWidth={isMobile}
              sx={{
                minWidth: isMobile ? "100%" : "140px",
                order: isMobile ? 2 : 1,
                color: "#26a6ae"
              }}
            >
              Remind me later
            </Button>
          </MaterialTooltip>

          <MaterialTooltip title="This overview won't be shown again." isShow placement="top">
            <Button
              onClick={handleGetStarted}
              variant="contained"
              fullWidth={isMobile}
              sx={{
                minWidth: isMobile ? "100%" : "140px",
                order: isMobile ? 1 : 2
              }}
            >
              Get started
            </Button>
          </MaterialTooltip>
        </Stack>
      </Stack>
    </Modal>
  );
};

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none"
};
